/**
 * NOTE: this file is used both inside and outside a JSX context.
 * Therefore it must not have any JSX dependencies.
 */

import { type ILead } from 'src/services/api/leads/types';
import type { IConversation } from 'src/api/landlord-messages-api';
import { type IProperty } from 'src/services/api';
import { EMessageChannel } from 'src/services/api/messages/types';
import type { IOtherUser, IUser } from 'src/api/users-api';
import { type IConversationMatch, type TConversationsFilter } from 'src/services/api/messages/use-conversations-search';

/**
 * We have a large variety of stages and sometimes we want to group similar stages together.
 */
export enum EConversationTab {
    ALL = 'All',
    NEW = 'New',
    CONTACTED = 'Contacted',
    TOUR = 'Tour',
    /**
     * Includes lease signed
     */
    APPLICATION = 'Application+',
    NOT_QUALIFIED = 'Not Qualified',
    NOT_INTERESTED = 'Not Interested',
}

export const conversationTabs = Object.values(EConversationTab);

export const isConversationTab = (v: any): v is EConversationTab => {
    if (typeof v !== 'string') {
        return false;
    }
    return Object.values(EConversationTab).includes(v as EConversationTab);
};

export enum EConversationCategory {
    Unread = 'Unread',
    Active = 'Active',
    Ghosted = 'Ghosted',
    Stale = 'Stale',
    Archived = 'Archived',
}

export const conversationCategories = Object.values(EConversationCategory);

export const initialConversationsPagesByCategory = conversationCategories.reduce((acc, cat) => {
    return {
        ...acc,
        [cat]: 1
    };
}, {} as Context['pagesByCategory']);

export type IConversationMatchWithChannel = IConversationMatch & {
    msg_channel: EMessageChannel;
};

/**
 * Return the best message channel for the conversation & lead
 * If there is no good channel, return null
 */
export function getBestChannelForConversation(conversation: IConversation | IConversationMatchWithChannel, lead?: ILead): EMessageChannel {
    let channel = isConversation(conversation) ?
        conversation.latest_message_channel :
        (conversation as IConversationMatchWithChannel).msg_channel;

    // prefer SMS over everything else if available
    if (lead?.ingestion_method === 'listing-site-email' && !!lead.phone_number) {
        channel = EMessageChannel.SMS;
    }

    return channel;
}

export function isConversation(conversation: IConversation | IConversationMatchWithChannel): conversation is IConversation {
    return Object.prototype.hasOwnProperty.call(conversation, 'num_unread_messages') && Object.prototype.hasOwnProperty.call(conversation, 'latest_message_channel');
}

export type TLandlordConversationsContext = {
    loggedInUser: IUser;
    teamMembers: IOtherUser[];
    leads: ILead[];
    leadsByUuid: Map<string, ILead>;
    properties: IProperty[];

    /**
     * All conversations, before filters are applied.
     */
    conversations: IConversation[] | undefined;
    // refetchConversations: () => void;
    /**
     * Conversations after filters are applied.
     */
    filteredConversations: IConversation[] | undefined;

    tab: EConversationTab;
    setTab: (k: EConversationTab) => void;
    conversationsByTabs: Record<EConversationTab, IConversation[]>;

    search: string;
    setSearch: Action<Context['search']>;
    isFilterOpen: boolean;
    setFilterOpen: Action<Context['isFilterOpen']>;
    filter: TConversationsFilter;
    setFilter: (k: Partial<TConversationsFilter>) => void;
    resetFilter: () => void;
    isSearchOrFilterActive: boolean;

    rowsPerPage: number;

    categories: EConversationCategory[];
    pagesByCategory: Record<EConversationCategory, number>;
    setPagesByCategory: Action<Context['pagesByCategory']>;
    conversationsByCategory: Record<EConversationCategory, IConversation[]>;
    visibleConversationsByCategory: Record<EConversationCategory, IConversation[]>;
    visibleConversations: IConversation[];

    /**
     * Conversations that are currently selected.
     * User can select multiple conversations at a time.
     * Conversations can be selected by clicking on the checkbox next to them or by pressing the space key while the conversation is focused.
     */
    selectedConversations: (IConversation | IConversationMatchWithChannel)[];
    setSelectedConversations: Action<Context['selectedConversations']>;
    /**
     * User can only have one focused conversation at a time.
     * A conversation can be focused with the up and down arrow keys.
     */
    focusedConversation?: IConversation;
    setFocusedConversation: Action<Context['focusedConversation']>;

    /**
     * Open that specific conversation, preserving the ability to go back to the list.
     */
    openConversation: (conversation: IConversation | IConversationMatchWithChannel, backPath: string) => void;

    mobileMenuAnchorEl?: HTMLElement;
    setMobileMenuAnchorEl: Action<Context['mobileMenuAnchorEl']>;
};

// Helper types to make the code more concise.
// Must not be exported!
type Action<T> = React.Dispatch<React.SetStateAction<T>>;
type Context = TLandlordConversationsContext;
